import { getCookie } from "@/lib/utils";
import { store } from "@/store/store";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const window: any;

interface ITrackerProps {
  event: string;
  data?: Record<string, unknown>;
}

export const DEFAULT_PACKAGE_ID = "general course";

interface ITrackerIdentifyProps {
  userId: string;
  traits: Record<string, unknown>;
}
const generateOfferingKeyIdentifier = (path: string) => {
  let cleanedPath = path.replace(/^\/|\/$/g, "");
  cleanedPath = cleanedPath.replace(/\//g, "_");
  return cleanedPath;
};

const globalAttr = () => {
  const state = store.getState();
  const universityName = process.env["NEXT_PUBLIC_UNIVERSITY_NAME"];
  const urlParams = new URL(window.location.toString()).searchParams;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { userAgentData } = window?.navigator as any;
  // Check if device is mobile using window width (breakpoint 800px)
  const isDeviceMobile =
    typeof window !== "undefined" ? window.innerWidth < 800 : false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const globalState = state.globalslice as any;
  const pageCategories = ["home_page", "program_page", "university_page"];
  const offeringKeyIdentifier = pageCategories.includes(
    globalState?.pageCategory
  )
    ? generateOfferingKeyIdentifier(window.location.pathname)
    : "";
  const userDetails = sessionStorage.getItem("u_details") || "";
  let userEmail = "";
  let leadId = "";
  if (userDetails && userDetails !== "undefined") {
    const parsedDetails = JSON.parse(userDetails);
    userEmail = parsedDetails?.email || "";
    leadId = parsedDetails?.id || "";
  }

  let resolvedPageCategory;

  if (
    globalState?.is_specialisation &&
    globalState?.pageCategory === "program_page"
  ) {
    if (globalState?.noOfSlugs === 2) {
      resolvedPageCategory = `${universityName?.toLowerCase()}_specialisation_page`;
    } else if (globalState?.noOfSlugs === 1) {
      resolvedPageCategory = `${universityName?.toLowerCase()}_spec_page`;
    } else {
      resolvedPageCategory = `${universityName?.toLowerCase()}_spec_page`;
    }
  } else if (globalState?.pageCategory === "home_page") {
    resolvedPageCategory = `${universityName?.toLowerCase()}_home_page`;
  } else if (globalState?.pageCategory === "program_page") {
    resolvedPageCategory = `${universityName?.toLowerCase()}_course_without_spec_page`;
  } else {
    resolvedPageCategory = globalState?.pageCategory || "";
  }

  return {
    business_entity: universityName?.toLowerCase(),
    country: globalState?.country,
    city: globalState?.city,
    state: globalState?.state,
    source_identifier: isDeviceMobile ? "mobile" : "web",
    device_type: isDeviceMobile ? "mobile_web" : "desktop_web",
    platform_os: userAgentData?.platform || "",
    platform_version: userAgentData?.brands?.[1]?.version || "",
    platform_agent: userAgentData?.brands?.[1]?.brand || "",
    session_id: parseInt(
      getCookie(`${universityName?.toLowerCase()}-sessionId`) || "0"
    ),
    experiment_name: "",
    page_category: resolvedPageCategory,
    page_url: document.URL,
    page_title: document.title,
    screen_resolution: `${window.screen.width}*${window.screen.height}`,
    logged_in_flag: sessionStorage.getItem("isUserVerified"),
    referrer: document.referrer,
    path: window.location.pathname,
    paid_user: "",
    offering_key_identifier: offeringKeyIdentifier,
    latest_utm_campaign: urlParams.get("utm_campaign") || "",
    latest_utm_medium: urlParams.get("utm_medium") || "",
    latest_utm_source: urlParams.get("utm_source") || "",
    latest_utm_term: urlParams.get("utm_term") || "",
    latest_utm_content: urlParams.get("utm_content") || "",
    university: globalState?.universityName
      ? globalState?.universityName.replaceAll(" ", "-").toLowerCase()
      : "",
    email_id: userEmail || "",
    lead_identifier: leadId || "",
  };
};

/**
 * Builds custom data object for Freshchat user properties.
 * Used by fcWidget.user.setProperties() to give chat context (ported from iitkgp).
 */
export const getFreshchatCustomData = (): Record<string, string> => {
  if (typeof window === "undefined") return {};
  const attrs = globalAttr();

  return {
    page_category: String(attrs.page_category || ""),
    path: String(attrs.path || ""),
    page_url: String(attrs.page_url || ""),
    page_title: String(attrs.page_title || ""),
    session_id: String(attrs.session_id ?? ""),
    offering_key_identifier: String(attrs.offering_key_identifier || ""),
    utm_source: String(attrs.latest_utm_source || ""),
    utm_medium: String(attrs.latest_utm_medium || ""),
    utm_campaign: String(attrs.latest_utm_campaign || ""),
    utm_content: String(attrs.latest_utm_content || ""),
    utm_term: String(attrs.latest_utm_term || ""),
    component_name: "chat_with_us",
    business_entity: String(attrs.business_entity || ""),
    source_identifier: String(attrs.source_identifier || ""),
    device_type: String(attrs.device_type || ""),
    referrer: String(attrs.referrer || ""),
    email_id: String(attrs.email_id || ""),
    lead_identifier: String(attrs.lead_identifier || ""),
    pageCategory: String(attrs.page_category || ""),
    programPackageKey: DEFAULT_PACKAGE_ID,
    Product_Package_Key: DEFAULT_PACKAGE_ID,
  };
};

// identity
export const identify = (props: ITrackerIdentifyProps): void => {
  const { userId, traits } = props;
  if (typeof window !== "undefined") {
    window?.rudderanalytics?.identify(userId, traits, {}, () => {
      console.info("Event Tracked success", { userId, traits });
    });
  }
};

// tracker
export const tracker = (props: ITrackerProps) => {
  const { event, data } = props;
  if (typeof window !== "undefined") {
    // Track with RudderStack
    if (window.rudderanalytics) {
      const defaultData = globalAttr();

      const eventFinalData = { ...defaultData, ...data };
      window.rudderanalytics.track(event, eventFinalData, {}, () => {
        console.info("Event Tracked success", { event, eventFinalData });
      });
    } else {
      console.error("RudderStack Not Loaded");
    }

    // Track with gtag / GTM dataLayer (ported from iitkgp). Without this no
    // Freshchat (or other) lead event reaches Google Ads / the dataLayer.
    if (typeof window.gtag === "function") {
      try {
        const defaultData = globalAttr();
        const eventFinalData = { ...defaultData, ...data };

        // Map internal events to gtag event names.
        let gtagEventName = "click";
        if (event === "lead_submitted") {
          gtagEventName = "lead_submitted";
        } else if (event === "auth_success") {
          gtagEventName = "formsubmit";
        } else if (event === "lead_info_captured") {
          gtagEventName = "formsubmit";
        }

        const gtagParams: Record<string, unknown> = {
          event_category: (data?.["section"] as string) || "general",
          event_label: (data?.["label"] as string) || event,
          ...eventFinalData,
        };

        window.gtag("event", gtagEventName, gtagParams);

        // Fire the Google Ads conversion when a lead is submitted (e.g. an
        // email captured in Freshchat). This is what was missing before.
        if (event === "lead_submitted") {
          const sendTo = process.env.NEXT_PUBLIC_GOOGLE_ADS_CONVERSION_ID;
          if (sendTo) {
            window.gtag("event", "conversion", {
              send_to: sendTo,
              ...(data?.["packageId"] != null && { packageId: data["packageId"] }),
              ...(data?.["entity_name"] != null && { entity_name: data["entity_name"] }),
              ...(data?.["section"] != null && { section: data["section"] }),
              ...(data?.["label"] != null && { label: data["label"] }),
            });
            console.log("✅ GTM conversion tracked:", { event, data });
          }
        }
      } catch (error) {
        console.log("Error tracking gtag event:", error);
      }
    }
  }
};
